/**
 * Distributor service - centralises all distributor-related API calls.
 *
 * Covers: distributor listing, invitation emails, nexus states,
 * and JSON:API model operations.
 */
import api from './api'

// ---------------------------------------------------------------------------
// Distributor Retrieval
// ---------------------------------------------------------------------------

export function getDistributorList(filterName, filterEmail, orderBy, limit) {
  return api.get(
    `/distributor/getDistributorList/${filterName}/${filterEmail}/${orderBy}/${limit}`
  )
}

export function getDistributorById(distributorId) {
  return api.get(`/distributors/${distributorId}`)
}

export function resendInvitationEmails() {
  return api.get('/distributor/resendInvitationEmails')
}

// ---------------------------------------------------------------------------
// Nexus States
// ---------------------------------------------------------------------------

export function getDistributorNexusStates(distributorId) {
  return api.get(`/distributor/getDistributorNexusStates/${distributorId}`)
}

export function editNexusStates(distributorId, args) {
  return api.post(`/distributors/${distributorId}/edit_nexus_states`, {
    meta: { method: 'edit_nexus_states', args },
  })
}

/**
 * Persist a distributor's tax-nexus jurisdictions (full state-name strings).
 *
 * Distinct from the legacy `editNexusStates` SAFRS RPC above, which is
 * gated on the broad `distributor.manage` capability. This dedicated
 * endpoint is gated on the Owner-only `distributor.compliance.manage`
 * capability - the correct authority surface for the compliance editor.
 *
 * @param {number} distributorId
 * @param {string[]} states  Full state names (e.g. ["California", "Texas"]).
 */
export function setDistributorNexusStates(distributorId, states) {
  return api.post(`/distributor/setDistributorNexusStates/${distributorId}`, {
    nexus_states: states,
  })
}

/**
 * When the Owner confirmed the sales-tax states list: `{ confirmed_at }`
 * (ISO-8601 UTC, or null). Signup auto-adds the company state, so the
 * "Sales tax states reviewed" gate needs this confirmation (Bug Intake #502).
 */
export function getNexusConfirmation(distributorId) {
  return api.get(`/distributor/nexusConfirmation/${distributorId}`)
}

/** Confirm the stored sales-tax states list (idempotent). Owner-only. */
export function confirmNexusStates(distributorId) {
  return api.post(`/distributor/confirmNexusStates/${distributorId}`)
}

// ---------------------------------------------------------------------------
// Notification Preferences (self-scoped; no id in URL)
// ---------------------------------------------------------------------------

/**
 * Read the authenticated Owner's notification-preferences matrix.
 *
 * Self-scoped server-side to g.account -- there is NO id in the URL, so an
 * actor can only ever read their own preferences. Response shape:
 *   { events: [ { event_key, label, group, compliance_critical,
 *                 channels: { in_app, email } }, ... 19 ] }
 */
export function getNotificationPreferences() {
  return api.get('/notifications/preferences')
}

/**
 * Persist per-event channel choices.
 *
 * @param {{ preferences: Array<{event_key: string, in_app: boolean,
 *           email: boolean}> }} payload
 * Returns the post-write state in the same shape as the GET (the server
 * clamps compliance-critical events back to in_app=true / email=true).
 */
export function setNotificationPreferences(payload) {
  return api.patch('/notifications/preferences', payload)
}

// ---------------------------------------------------------------------------
// Reporting -- distributor performance basic stats (self-scoped)
// ---------------------------------------------------------------------------

/**
 * Read GMV / order stats, the GMV trend, and top products for the actor.
 *
 * Scope is server-enforced (Owner sees all distributor stores; Builder is
 * scoped to their own). Response shape:
 *   { scope: "owner"|"builder",
 *     stats: { total_stores, total_orders, total_gmv, avg_order_value },
 *     range: { start_date, end_date },   // echoed back; null = unbounded
 *     bucket: "day"|"week"|"month",
 *     gmv_series: [{ bucket_start, gmv, orders }],   // sums to total_gmv
 *     top_products: [{ product_id, product_name, gmv, units }] }
 *
 * `startDate` / `endDate` are `YYYY-MM-DD` and each INCLUSIVE of the whole
 * day. Omit both for all time, which is the default -- an absent bound is
 * left OFF the request entirely rather than sent empty, so the server sees
 * the same unfiltered call it saw before this feature existed.
 *
 * The snake_case translation to the wire happens HERE, in the one place that
 * owns the query string, rather than leaking the server's naming into the
 * component (and past the camelcase lint rule) at every call site.
 *
 * @param {{startDate?: string, endDate?: string}} [range]
 */
export function getDistributorPerformance({ startDate, endDate } = {}) {
  const params = {}
  if (startDate) params.start_date = startDate
  if (endDate) params.end_date = endDate
  return api.get('/reporting/distributor-performance', { params })
}

/**
 * Read the actor's cross-store order list (newest-first, paginated).
 *
 * Backs the Distributor App Orders surface
 * (card https://trello.com/c/pSDstQD3). Scope is server-enforced: an Owner
 * sees every store under the distributor; a Builder is scoped to the stores
 * they created. Response shape (LOCKED backend contract, PR #1818; `stores`
 * added additively with the filter UI):
 *   { scope: "owner"|"builder", total: <int>,
 *     orders: [ { id, order_number (nullable), store_id, store_name,
 *                 placed_at (iso8601), status, shopper_name,
 *                 total_dollars (number, never null) } ],
 *     stores: [ { id, name } ] }
 * Newest-first is applied server-side; `limit` is capped at 100 server-side,
 * so no page-size control may offer more than 100.
 *
 * `total` is the FILTERED count, so a range label built from it stays honest
 * while a filter is active.
 *
 * Filter params (`q`, `store_id`, `date_from`, `date_to`) are optional and
 * passed straight through. This function deliberately does NOT normalize
 * them -- the caller decides what "no filter" means and simply omits the
 * key, so only one layer holds that convention.
 *
 * @param {{limit?: number, offset?: number, q?: string, store_id?: number,
 *          date_from?: string, date_to?: string}} [params]
 */
export function getDistributorOrders({ limit = 25, offset = 0, ...filters } = {}) {
  return api.get('/reporting/distributor-orders', {
    params: { limit, offset, ...filters },
  })
}

/**
 * Read one order's batch and shipping for the distributor order-detail page.
 *
 * Same scope as the list above (server-enforced; an order outside it is a
 * 404). Response:
 *   { order_id, batch: { id, name, state, store_id } | null,
 *     shipping: { recipient_name?, address1?, address2?, city?, state?,
 *                 postal_code?, country? } | null,
 *     shipments: [ { carrier, tracking_number, tracking_url, shipped_at } ] }
 * `shipping` is the order's frozen ship-to, the same block the store's
 * Batches page shows; empty fields are omitted, and it is null when the
 * order has no address.
 *
 * @param {number|string} orderId
 */
export function getDistributorOrderFulfillment(orderId) {
  return api.get(`/reporting/distributor-orders/${orderId}/fulfillment`)
}

/**
 * GET /api/reporting/distributor-payouts
 * Returns payout figures derived from order_split for all scoped orders.
 * @returns {Promise}
 */
export function getDistributorPayouts() {
  return api.get('/reporting/distributor-payouts')
}


// ---------------------------------------------------------------------------
// SA tenant enable / disable (soft-delete)
// ---------------------------------------------------------------------------

/**
 * Enable or disable a distributor (Super Admin only).
 *
 * THE SINGLE FRONTEND WRITE PATH for this action. Jake chose to expose the
 * control on BOTH SA surfaces -- a row action on the distributor list and a
 * section on the distributor edit page -- and accepted that this means two
 * call sites. Both call THIS function; neither POSTs for itself. Two entry
 * points, one mechanism (design-philosophy "one write model").
 *
 * Maps onto the existing SA-only endpoint pair, which is where the real work
 * lives: setting / clearing `distributors.disabled_at`, pausing or resuming
 * billing to the EXACT pre-disable state, and writing the `override` audit
 * row. Both endpoints are idempotent and gated on `superadmin.tenants` (by
 * decorator AND by an `is_unscoped_caller()` check in the body).
 *
 * Consequence, because callers should not have to go read the backend to
 * learn it: disabling a distributor stops every one of its users from signing
 * in, takes its storefronts offline, and suspends its billing. It is
 * reversible -- restore returns the tenant and its billing to exactly the
 * state it was in -- but it is not a quiet change, so both call sites gate
 * the disable direction behind an explicit confirmation.
 *
 * Returns the SERVER's post-write state, not the requested one. Because the
 * endpoints are idempotent, `changed` can legitimately be false (the tenant
 * was already in the requested state) -- a caller that rendered its own
 * request would be showing a state the server may never have entered.
 *
 * @param {number|string} distributorId
 * @param {boolean} disabled  true -> disable, false -> enable (restore).
 * @returns {Promise<{id: number, changed: boolean, disabled: boolean}>}
 */
export async function setDistributorDisabled(distributorId, disabled) {
  const action = disabled ? 'disable' : 'restore'
  const response = await api.post(`/distributor/${distributorId}/${action}`)
  return response.data
}

// ---------------------------------------------------------------------------
// EIN (tax id) -- stored encrypted; the API only ever returns the last four.
// ---------------------------------------------------------------------------

/** @returns {Promise<{data: {has_ein: boolean, ein_last4: string|null, replace_available: boolean}}>} */
export function getEinStatus(distributorId) {
  return api.get(`/distributor/${distributorId}/ein`)
}

export function setEin(distributorId, ein) {
  return api.put(`/distributor/${distributorId}/ein`, { ein })
}

export function clearEin(distributorId) {
  return api.delete(`/distributor/${distributorId}/ein`)
}
